import { useState } from "react";
import { toast } from "sonner";
import { ChevronDown, Link2, Wand2 } from "lucide-react";
import { api, errMsg } from "../lib/api";

const slugOf = (n) => n.replace(/\W+/g, "-").toLowerCase();
const said = (x) => (x.how === "merged" ? `${x.name} → ${x.into}${x.records ? ` (${x.records} records)` : ""}` : `${x.name} → ${x.login}`);
const summary = (r) => {
  const steps = r.steps ? ` · ${r.steps} production step(s) now on their login` : "";
  return r.links.length ? `Linked ${r.links.map(said).join(", ")}${steps}` : r.steps ? `${r.steps} production step(s) now on their login` : "No clear matches left. Link the rest by hand.";
};

const NameRow = ({ n, users, onDone }) => {
  const [uid, setUid] = useState("");
  const go = () => api.post("/admin/tracker-names/link", { name: n.name, user_id: uid })
    .then((r) => { toast.success(r.data.how === "merged" ? `${n.name} merged into ${r.data.into} (${r.data.records} records)` : `${n.name} linked to ${r.data.login}`); onDone(); })
    .catch((e) => toast.error(errMsg(e)));
  return (
    <div className="flex items-center gap-2 border px-2 py-1.5 text-xs min-w-0" data-testid={`unlinked-name-${slugOf(n.name)}`}>
      <span className="font-medium truncate">{n.name}</span>
      <span className="text-muted-foreground shrink-0">{n.records ?? 0} records</span>
      {users && <>
        <select data-testid={`unlinked-name-login-${slugOf(n.name)}`} value={uid} onChange={(e) => setUid(e.target.value)} className="ml-auto bg-muted/60 border h-7 px-1 text-xs w-36 min-w-0">
          <option value="">Link to login…</option>{users.map((u) => <option key={u.user_id} value={u.user_id}>{u.name}{u.staff_name ? ` (${u.staff_name})` : ""}</option>)}</select>
        <button data-testid={`unlinked-name-link-${slugOf(n.name)}`} disabled={!uid} onClick={go} title="Link" className="border h-7 w-7 flex items-center justify-center hover:border-primary disabled:opacity-40 shrink-0"><Link2 className="h-3.5 w-3.5" /></button>
      </>}
    </div>
  );
};

export const TrackerLinks = ({ names, users, canEdit, onDone }) => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const un = names.filter((n) => !n.linked_to);
  if (!un.length && !canEdit) return null;
  const logins = canEdit ? users.filter((u) => u.status === "active") : null;
  const sync = () => {
    setBusy(true);
    api.post("/admin/tracker-names/sync").then((r) => { toast.success(summary(r.data), { duration: 12000 }); onDone(); })
      .catch((e) => toast.error(errMsg(e))).finally(() => setBusy(false));
  };
  return (
    <div className="ax-panel p-3 mb-4" data-testid="unlinked-names">
      <div className="flex flex-wrap items-center gap-2">
        <button data-testid="unlinked-names-toggle" onClick={() => setOpen(!open)} className="flex items-center gap-1 text-left">
          <span className="ax-label">Tracker names not linked to a login ({un.length})</span><ChevronDown className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`} /></button>
        {canEdit && <button data-testid="tracker-names-sync" disabled={busy} onClick={sync} className="ml-auto border h-8 px-3 text-xs flex items-center gap-1.5 hover:border-primary disabled:opacity-50">
          <Wand2 className="h-3.5 w-3.5" />{busy ? "Matching…" : "Match names to logins"}</button>}
      </div>
      {open && <>
        <p className="text-xs text-muted-foreground my-2">Leads, UIDs, stage-owner reminders and production steps reach a person only when their tracker name is linked to their login.
          "Match names to logins" links the names that clearly belong to one login, like Dharmender → Dharmendra Kumar.{canEdit ? " Pick a login to link the rest. If that login already has a name, this spelling is merged into it (you can undo it under name spellings below)." : " Only founders can link names."}</p>
        {!un.length ? <p className="text-xs text-emerald-700" data-testid="unlinked-names-none">Every tracker name is linked.</p> : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-1.5">{un.map((n) => <NameRow key={n.name} n={n} users={logins} onDone={onDone} />)}</div>)}
      </>}
    </div>
  );
};
